import { createFileRoute, Link } from '@tanstack/react-router'

export const Route = createFileRoute('/download')({
  head: () => ({
    meta: [
      {
        title: 'Download Provenance — iOS & Android | WatchCentral',
      },
      {
        name: 'description',
        content:
          'Download the free Provenance app for iOS and Android. View and transfer digital product passports, run the acoustic Time Grapher, and manage intake requests — on both platforms.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://provenance.watchcentral.com/download' },
      {
        property: 'og:title',
        content: 'Download Provenance — iOS & Android | WatchCentral',
      },
      {
        property: 'og:description',
        content:
          'Free for owners. Download the Provenance app on iOS 16+ or Android 11+.',
      },
      { name: 'twitter:card', content: 'summary' },
      {
        name: 'twitter:title',
        content: 'Download Provenance — iOS & Android',
      },
    ],
    links: [
      { rel: 'canonical', href: 'https://provenance.watchcentral.com/download' },
    ],
  }),
  component: DownloadPage,
})


function Nav() {
  return (
    <nav
      className="sticky top-0 z-50 border-b border-[var(--line)] bg-[rgba(17,19,34,0.82)] backdrop-blur-[14px]"
      aria-label="Main navigation"
    >
      <div className="mx-auto flex h-16 max-w-[1320px] items-center justify-between px-[var(--gutter)]">
        <Link
          to="/"
          className="flex items-center gap-[10px] font-mono text-[13px] font-[600] tracking-[0.28em] uppercase text-ink"
          aria-label="Provenance — Home"
        >
          <span className="brand-mark" aria-hidden="true" />
          Provenance
          <span className="ml-1 border-l border-[var(--line-strong)] pl-[10px] font-mono text-[9px] font-[400] tracking-[0.2em] text-ink-mute">
            by WatchCentral
          </span>
        </Link>

        <div className="hidden items-center gap-7 md:flex">
          <Link
            to="/"
            hash="product"
            className="font-mono text-[11px] font-[500] tracking-[0.14em] uppercase text-ink-soft transition-colors hover:text-ink"
          >
            Product
          </Link>
          <Link
            to="/download"
            className="font-mono text-[11px] font-[500] tracking-[0.14em] uppercase text-ink transition-colors"
            aria-current="page"
          >
            Download
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="/login"
            className="hidden font-mono text-[11px] font-[500] tracking-[0.12em] uppercase border border-[var(--line-strong)] px-[14px] py-[10px] text-ink transition-colors hover:border-ink-soft sm:inline-flex"
          >
            Sign in
          </a>
          <Link
            to="/download"
            className="inline-flex items-center gap-[10px] bg-accent border border-accent px-[14px] py-[10px] font-mono text-[11px] font-[600] tracking-[0.12em] uppercase text-accent-ink"
            aria-current="page"
          >
            Get the app
          </Link>
        </div>
      </div>
    </nav>
  )
}

function Footer() {
  return (
    <footer className="mt-20 border-t border-[var(--line)] bg-bg-2 pb-8 pt-16">
      <div className="mx-auto max-w-[1320px] px-[var(--gutter)]">
        <div className="mb-14 grid gap-12 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr]">
          <div>
            <Link
              to="/"
              className="mb-4 flex items-center gap-[10px] font-mono text-[13px] font-[600] tracking-[0.28em] uppercase text-ink"
            >
              <span className="brand-mark" aria-hidden="true" />
              Provenance
            </Link>
            <p className="mt-2 max-w-[34ch] text-[13px] text-ink-soft">
              Digital product passports for fine watches. Issued, audited, and
              transferred under the authority of WatchCentral.
            </p>
            <p className="mt-6 font-mono text-[10px] tracking-[0.14em] uppercase text-ink-mute">
              WatchCentral · New York
            </p>
          </div>
          <nav aria-label="Product links">
            <h3 className="mb-4 font-mono text-[11px] tracking-[0.14em] uppercase text-ink-mute font-[500]">
              Product
            </h3>
            <ul className="flex flex-col gap-[10px] list-none p-0 m-0">
              <li><Link to="/" hash="product" className="text-[13px] text-ink-soft hover:text-ink">Overview</Link></li>
              <li><Link to="/download" className="text-[13px] text-ink-soft hover:text-ink">Download</Link></li>
            </ul>
          </nav>
          <nav aria-label="Company links">
            <h3 className="mb-4 font-mono text-[11px] tracking-[0.14em] uppercase text-ink-mute font-[500]">
              Company
            </h3>
            <ul className="flex flex-col gap-[10px] list-none p-0 m-0">
              <li><a href="mailto:sales@watchcentral.com" className="text-[13px] text-ink-soft hover:text-ink">sales@watchcentral.com</a></li>
              <li><a href="/terms" className="text-[13px] text-ink-soft hover:text-ink">Terms</a></li>
              <li><a href="/privacy" className="text-[13px] text-ink-soft hover:text-ink">Privacy</a></li>
            </ul>
          </nav>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[var(--line)] pt-6 font-mono text-[10px] tracking-[0.12em] uppercase text-ink-mute">
          <span>© 2026 WatchCentral · All rights reserved</span>
          <span>Provenance v2.4.1</span>
        </div>
      </div>
    </footer>
  )
}


function QrCode({ label }: { label: string }) {
  return (
    <div
      className="relative overflow-hidden bg-ink"
      style={{ width: 120, height: 120 }}
      aria-label={`QR code to download Provenance on ${label}`}
      role="img"
    >
      {}
      <div
        className="absolute inset-2"
        style={{
          background: `
            conic-gradient(from 0deg at 30% 30%, #EFF1F5 0 70deg, #111322 70deg 140deg, #EFF1F5 140deg 210deg, #111322 210deg 280deg, #EFF1F5 280deg),
            repeating-linear-gradient(0deg, #111322 0 4px, #EFF1F5 4px 8px),
            repeating-linear-gradient(90deg, #111322 0 4px, #EFF1F5 4px 8px)
          `,
          backgroundBlendMode: 'difference, normal, difference',
        }}
      />
      {}
      {(['tl', 'tr', 'bl'] as const).map((pos) => (
        <div
          key={pos}
          className="absolute flex items-center justify-center bg-bg"
          style={{
            width: 28,
            height: 28,
            top: pos === 'bl' ? undefined : 8,
            bottom: pos === 'bl' ? 8 : undefined,
            left: pos === 'tr' ? undefined : 8,
            right: pos === 'tr' ? 8 : undefined,
          }}
          aria-hidden="true"
        >
          <div className="bg-ink" style={{ width: 18, height: 18 }} />
        </div>
      ))}
    </div>
  )
}


interface PlatformSpec {
  k: string
  v: string
  badge?: string
}

interface PlatformCardProps {
  index: string
  name: string
  icon: React.ReactNode
  scanLabel: string
  scanSub: string
  storeLabel: string
  specs: PlatformSpec[]
  storeHref?: string
}

function PlatformCard({
  index,
  name,
  icon,
  scanLabel,
  scanSub,
  storeLabel,
  specs,
  storeHref = '#',
}: PlatformCardProps) {
  return (
    <div className="flex flex-col p-12 sm:p-12 md:p-12" style={{ padding: 48 }}>
      {}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="mb-1 font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
            Platform / {index}
          </div>
          <h2 className="font-mono text-[32px] font-[500] m-0 text-ink">{name}</h2>
        </div>
        <div className="flex h-12 w-12 items-center justify-center border border-[var(--line-strong)] text-ink">
          {icon}
        </div>
      </div>

      {}
      <div className="mb-6 grid items-center gap-5 border border-[var(--line)] bg-bg p-5" style={{ gridTemplateColumns: '120px 1fr' }}>
        <QrCode label={name} />
        <div className="flex flex-col gap-1.5">
          <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-accent">
            ↗ Install
          </span>
          <span className="font-mono text-[18px] font-[500] text-ink">{scanLabel}</span>
          <span className="text-[12px] text-ink-soft">{scanSub}</span>
        </div>
      </div>

      {}
      <ul className="m-0 list-none border-t border-[var(--line)] p-0 mb-8">
        {specs.map((s) => (
          <li
            key={s.k}
            className="grid items-start gap-4 border-b border-[var(--line)] py-3 text-[13px]"
            style={{ gridTemplateColumns: '90px 1fr auto' }}
          >
            <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-ink-mute">
              {s.k}
            </span>
            <span className="text-ink">{s.v}</span>
            {s.badge && (
              <span className="font-mono text-[9px] tracking-[0.12em] uppercase text-accent">
                {s.badge}
              </span>
            )}
          </li>
        ))}
      </ul>

      {}
      <a
        href={storeHref}
        className="flex w-full items-center justify-center gap-[10px] bg-accent border border-accent px-7 py-[18px] font-mono text-[13px] font-[600] tracking-[0.14em] uppercase text-accent-ink transition-colors hover:bg-accent-dark hover:border-accent-dark mt-auto"
        target="_blank"
        rel="noopener noreferrer"
      >
        {storeLabel}
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="M3 7h8M7 3l4 4-4 4" />
        </svg>
      </a>
    </div>
  )
}


const IOS_SPECS: PlatformSpec[] = [
  { k: 'Version', v: '2.4.1', badge: 'Stable' },
  { k: 'Size', v: '23.8 MB', badge: '↓ 14%' },
  { k: 'Requires', v: 'iOS 16.0 or later' },
  { k: 'Devices', v: 'iPhone · iPad' },
  { k: 'Languages', v: 'EN · FR · IT · DE · JA' },
  { k: 'Updated', v: 'May 19, 2026' },
]

const ANDROID_SPECS: PlatformSpec[] = [
  { k: 'Version', v: '2.4.1', badge: 'Stable' },
  { k: 'Size', v: '24.6 MB', badge: 'APK / AAB' },
  { k: 'Requires', v: 'Android 11.0 or later' },
  { k: 'Devices', v: 'Phone · Tablet' },
  { k: 'Languages', v: 'EN · FR · IT · DE · JA' },
  { k: 'Updated', v: 'May 19, 2026' },
]

const CHANGELOG = [
  {
    version: 'v2.4.1',
    date: 'May 19, 2026',
    notes: [
      'Time Grapher: beat-error noise floor reduced ~30%; better lift-angle inference for 21,600 BPH.',
      'DPP detail: rich history entries with operator signatures, ISO timestamps and block anchors.',
      'Intake: Warranty Estimate form now supports comprehensive coverage and $0 deductible.',
    ],
  },
  {
    version: 'v2.4.0',
    date: 'Apr 02, 2026',
    notes: [
      'New: DPP Transfer flow (operator-reviewed handoff between holders).',
      'Account: explicit Light / Dark / System appearance toggle.',
      'Many small fixes to keyboard handling on iPhone 15 Pro Max.',
    ],
  },
  {
    version: 'v2.3.4',
    date: 'Mar 11, 2026',
    notes: [
      'Watch identification: confidence intervals on suggested reference numbers.',
      'Provenance Inquiry: attach up to 8 reference photographs (was 4).',
    ],
  },
  {
    version: 'v2.3.2',
    date: 'Feb 24, 2026',
    notes: [
      'Android: signed-app distribution moved to Play Store internal track.',
      'Service Request: rush 5–7 day option added for express bench work.',
    ],
  },
]

const MODULES = [
  {
    num: '01 · DPP',
    title: 'Passports',
    desc: 'View, share and transfer every passport you hold. Photographs, specification and history travel with you.',
  },
  {
    num: '02 · TIME',
    title: 'Time Grapher',
    desc: 'Acoustic rate, amplitude and beat-error analysis. Trend movement performance run-over-run.',
  },
  {
    num: '03 · INTAKE',
    title: 'Intake',
    desc: 'Service requests, transfers, provenance inquiries and warranty estimates — routed to WatchCentral ops.',
  },
  {
    num: '04 · ACCOUNT',
    title: 'Account',
    desc: 'Appearance, notifications, currency. Help center, terms and direct support — built in.',
  },
]


function DownloadPage() {
  return (
    <>
      <Nav />
      <main id="main-content">

        {}
        <div className="mx-auto max-w-[1320px] px-[var(--gutter)]">
          <div className="py-16 pb-8">
            <div className="mb-6 font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
              <b className="font-[500] text-accent">↗</b> SECTION 06 — DOWNLOAD
            </div>
            <h1
              className="font-mono font-[500] leading-[0.98] tracking-[-0.02em] text-ink"
              style={{ fontSize: 'clamp(40px, 6vw, 76px)' }}
            >
              Provenance, for iOS<br />and Android.
            </h1>
            <p
              className="mt-8 text-ink-soft"
              style={{ fontSize: 'clamp(16px, 1.4vw, 19px)', lineHeight: 1.55, maxWidth: '64ch' }}
            >
              The mobile app holds your passports, runs the acoustic Time
              Grapher, and routes intake forms to WatchCentral operations. Free for
              owners, on both platforms.
            </p>
          </div>

          {}
          <div
            className="bracket-frame mt-12 grid border border-[var(--line)] bg-surface"
            style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}
            aria-label="Download options"
          >
            <span className="bk-bl" aria-hidden="true" />
            <span className="bk-br" aria-hidden="true" />

            <div className="border-r border-[var(--line)]">
              <PlatformCard
                index="01"
                name="iOS"
                icon={
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M16.4 12.5c0-2.3 1.9-3.4 2-3.4-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.9-.8-3.1-.8-1.6 0-3 .9-3.8 2.4-1.6 2.8-.4 6.9 1.2 9.2.8 1.1 1.7 2.3 2.9 2.3 1.2 0 1.6-.7 3-.7 1.4 0 1.8.7 3 .7 1.2 0 2-1.1 2.8-2.2.9-1.3 1.2-2.5 1.3-2.6-.1 0-2.4-1-2.4-3zM14 4.4c.6-.8 1.1-1.9.9-3-1 .1-2.1.7-2.8 1.5-.6.7-1.1 1.8-1 2.9 1.1.1 2.2-.6 2.9-1.4z" />
                  </svg>
                }
                scanLabel="Scan with iPhone"
                scanSub='Or open the App Store and search "Provenance".'
                storeLabel="Open in App Store"
                specs={IOS_SPECS}
              />
            </div>

            <PlatformCard
              index="02"
              name="Android"
              icon={
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M3.6 1.8c-.3.3-.5.8-.5 1.4v17.5c0 .6.2 1.1.5 1.4l.1.1L13.4 12 3.7 1.7l-.1.1zM17 8.4l-3.6 3.6L17 15.6l4.4-2.5c1.2-.7 1.2-1.8 0-2.5L17 8.4zm-1.1-.6L4.7 1.4c-.4-.2-.8-.3-1.1-.1l11 11 1.3-3.5zM4.7 22.6l11.2-6.4-1.3-1.3L3.6 22.7c.3.2.7.1 1.1-.1z" />
                </svg>
              }
              scanLabel="Scan with Android"
              scanSub='Or open Google Play and search "Provenance WatchCentral".'
              storeLabel="Open in Google Play"
              specs={ANDROID_SPECS}
            />
          </div>
        </div>

        {}
        <section
          className="py-[clamp(64px,9vw,128px)]"
          aria-labelledby="modules-heading"
        >
          <div className="mx-auto max-w-[1320px] px-[var(--gutter)]">
            <div className="border border-[var(--line)]">
              <div className="flex items-center justify-between border-b border-[var(--line)] px-8 py-5">
                <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
                  Modules — installed by default
                </span>
                <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
                  4 / 4 ACTIVE
                </span>
              </div>

              <div
                className="grid"
                style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}
                role="list"
                aria-label="App modules"
              >
                {MODULES.map((m, i) => (
                  <div
                    key={m.num}
                    className={`flex min-h-[220px] flex-col gap-3 p-7 pb-9 ${i < MODULES.length - 1 ? 'border-r border-[var(--line)]' : ''}`}
                    role="listitem"
                  >
                    <span className="font-mono text-[11px] tracking-[0.14em] text-accent">
                      {m.num}
                    </span>
                    <h3 className="font-mono text-[17px] font-[500] m-0 text-ink">
                      {m.title}
                    </h3>
                    <p className="flex-1 text-[13px] leading-[1.55] text-ink-soft m-0">
                      {m.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {}
        <section
          className="py-[clamp(48px,6vw,80px)]"
          aria-labelledby="changelog-heading"
        >
          <div className="mx-auto max-w-[1320px] px-[var(--gutter)]">
            {}
            <div className="mb-8 grid grid-cols-[auto_1fr_auto] items-end gap-6 border-b border-[var(--line)] pb-6">
              <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
                <span className="text-accent">●</span> CHANGELOG · LAST 90 DAYS
              </div>
              <h2
                id="changelog-heading"
                className="font-mono font-[500] leading-[1.1] tracking-[-0.01em] text-ink m-0"
                style={{ fontSize: 'clamp(20px, 2.2vw, 28px)' }}
              >
                Recent releases
              </h2>
              <a
                href="#"
                className="inline-flex items-center gap-2 border-b border-transparent pb-[2px] font-mono text-[11px] font-[500] tracking-[0.14em] uppercase text-accent transition-colors hover:border-accent"
              >
                All releases
                <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-3 w-3" aria-hidden="true">
                  <path d="M3 7h8M7 3l4 4-4 4" />
                </svg>
              </a>
            </div>

            <div role="list" aria-label="Release notes">
              {CHANGELOG.map((entry) => (
                <div
                  key={entry.version}
                  className="grid items-start gap-6 border-b border-[var(--line)] py-[18px] last:border-b-0"
                  style={{ gridTemplateColumns: '100px 80px 1fr' }}
                  role="listitem"
                >
                  <div className="font-mono text-[13px] tracking-[0.06em] text-ink">
                    {entry.version}
                  </div>
                  <div className="font-mono text-[11px] tracking-[0.12em] uppercase text-ink-mute">
                    {entry.date}
                  </div>
                  <div className="text-[14px] leading-[1.6] text-ink-soft">
                    <ul className="m-0 pl-[18px]">
                      {entry.notes.map((n) => (
                        <li key={n} className="mb-1">
                          {n}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  )
}
